import { useEffect, useRef, useState } from "react";
import { useOrder } from "../context/OrderContext";
import api from "../lib/api";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Renders an overlay while we poll Stripe for the result of a Hosted Checkout
 * session. Mounts only when `?session_id=...` is present in the URL.
 *
 * On success: hop to the upsell step, scrub the URL, toast, return null.
 * On expiry/error: show retry/back buttons.
 *
 * Polls `/api/orders/{orderId}/payment-status?session_id=...` up to 6 times
 * with a 2s gap (12s total) — matches the playbook's recommendation.
 */
export default function PaymentReturnGuard({ children }) {
  const { state, goTo } = useOrder();
  const [phase, setPhase] = useState("idle"); // idle | polling | success | failed | expired
  const [message, setMessage] = useState("");
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const urlOrderId = params.get("order_id");
    if (!sessionId) return;
    startedRef.current = true;

    const orderId = urlOrderId || state.orderId;
    if (!orderId) {
      setPhase("failed");
      setMessage("We couldn't identify your order. Please check your email for confirmation.");
      return;
    }

    setPhase("polling");
    let attempts = 0;
    const maxAttempts = 6;

    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/orders/${orderId}/payment-status`, {
          params: { session_id: sessionId },
        });
        if (r.data?.payment_status === "paid") {
          setPhase("success");
          // Scrub the session_id from the URL so a refresh doesn't re-poll.
          const cleanUrl = `${window.location.origin}${window.location.pathname}`;
          window.history.replaceState({}, "", cleanUrl);
          toast.success("Payment received!");
          // Land the customer on the post-purchase upsell step AND dismiss the
          // overlay (otherwise children never re-render and the user is stuck
          // on the "Loading your order…" screen forever).
          setTimeout(() => {
            goTo("upsell");
            setPhase("idle");
          }, 600);
          return;
        }
        if (r.data?.status === "expired") {
          setPhase("expired");
          setMessage("Your checkout session expired. Please try again.");
          return;
        }
      } catch (e) {
        // network blip; retry
      }
      if (attempts >= maxAttempts) {
        setPhase("failed");
        setMessage("We couldn't confirm your payment yet. If you completed payment, check your email for a receipt.");
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
  }, [state.orderId, goTo]);

  if (phase === "idle") return children;

  return (
    <div data-testid="payment-return-guard"
      className="fixed inset-0 z-50 flex items-center justify-center bg-white">
      <div className="max-w-sm w-full p-8 text-center">
        {phase === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[var(--brand-green)]" />
            <h2 className="text-xl font-bold mt-4 text-[var(--brand-text)]">Confirming your payment…</h2>
            <p className="text-sm muted-text mt-2">Just a few seconds.</p>
          </>
        )}
        {phase === "success" && (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-[var(--brand-green)]" />
            <h2 className="text-xl font-bold mt-4 text-[var(--brand-text)]">Payment received!</h2>
            <p className="text-sm muted-text mt-2">Loading your order…</p>
          </>
        )}
        {(phase === "failed" || phase === "expired") && (
          <>
            <AlertCircle className="w-10 h-10 mx-auto text-amber-500" />
            <h2 className="text-xl font-bold mt-4 text-[var(--brand-text)]">
              {phase === "expired" ? "Session expired" : "Payment status unclear"}
            </h2>
            <p className="text-sm muted-text mt-2">{message}</p>
            <button
              data-testid="payment-return-retry"
              onClick={() => {
                const clean = `${window.location.origin}${window.location.pathname}`;
                window.history.replaceState({}, "", clean);
                window.location.reload();
              }}
              className="cta-btn mt-6 w-full"
            >
              Back to checkout
            </button>
          </>
        )}
      </div>
    </div>
  );
}
